/**
 * Copyright (c) 2022 - Present. Blend Labs, Inc. All rights reserved
 * Blend Confidential - Restricted
 */
import { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation } from 'react-router';
import { setDocuments, setSelectedDocument } from '../../../actions/documentActions';
import { notaryAPI } from '../../../api/notary';
import { participantAPI } from '../../../api/participant';
import { useInterval } from '../../../hooks/useInterval';
import { useRootSelector } from '../../../hooks/useRootSelector';
import { useRoom } from '../../../hooks/useRoom';
import { getSelectedDocumentPdfUrl, getSelectedRoomDocumentPdfUrl } from '@utils/documents';
import { isNotaryRoute } from '@utils/routing';
import { isEqual } from '@utils/lodash';
import { ActiveCallStatuses } from '@shared/constants/call';

const DOCUMENTS_REFRESH_INTERVAL = 4000;

export const useDocuments = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const { roomId } = useRoom();
  const isNotary = isNotaryRoute(location.pathname);
  const { callDetails, document } = useRootSelector(state => state);
  const { documents, selectedDoc } = document;

  const fetchDocuments = async () => {
    try {
      if (callDetails?.id) {
        const docs = isNotary
          ? await notaryAPI.documents.fetchCallDocuments(callDetails.id)
          : await participantAPI.documents.fetchCallDocuments(callDetails.id);
        if (!isEqual(documents, docs)) {
          setDocuments(docs)(dispatch);
        }
      }
    } catch (e) {
      throw e;
    }
  };

  useInterval(fetchDocuments, DOCUMENTS_REFRESH_INTERVAL, [callDetails?.id]);

  // once documents have loaded, automatically set selected doc unless signing is in progress
  useEffect(() => {
    const setSelectedDocumentAndFetchPdf = async () => {
      if (!callDetails || !documents) {
        return;
      }

      // don't update selected doc if signing has started - this will be handled by polling for current signing signingAction
      const shouldUpdateSelectedDoc = !selectedDoc && callDetails.status !== 'SIGNING' && documents.length;
      if (shouldUpdateSelectedDoc) {
        let fileUrl;
        // If this is the notary, we need to check if it is before or after signing start
        // We render the room document before signing start and call
        // document after since we do not create the call doc until signing start
        // For the participant, the call will always be active so we always render call document
        if (isNotary) {
          if (callDetails?.status && !ActiveCallStatuses.includes(callDetails.status)) {
            fileUrl = await getSelectedRoomDocumentPdfUrl(documents[0].id, roomId, documents);
          } else {
            fileUrl = await getSelectedDocumentPdfUrl(documents[0].id, callDetails, documents, isNotary);
          }
        } else {
          fileUrl = await getSelectedDocumentPdfUrl(documents[0].id, callDetails, documents, isNotary);
        }
        setSelectedDocument({
          id: documents[0].id,
          pdfUrl: fileUrl,
        })(dispatch);
      }
    };

    setSelectedDocumentAndFetchPdf();
  }, [documents, callDetails, selectedDoc, isNotary, dispatch]);
};
